import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OPDService } from '../../opd/opd.service';
import { IpdService } from '../ipd.service';
import { Iipd, IoperationSummary } from '../Iipd.type';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil, of, catchError, finalize } from 'rxjs';
import { TemplateListComponent } from '../../template/template-list/template-list.component';
import { TemplateType } from 'src/app/common/enums/template-type';
import { HttpClient } from '@angular/common/http';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { UserService } from '../../user/user.service';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { environment } from 'src/environments/environment';
import { IPatientInfo } from '../../patient/ipatient-info';
import { Iopd } from '../../opd/opddetails/iopddetails';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { ConvertTo24HourPipe } from 'src/app/pipe/convert-to24-hour.pipe';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';

@Component({
  selector: 'app-ipd-discharge',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TablerIconsModule,
    TranslateModule,
    // LoaderComponent,
  ],
  providers: [
    ConvertTo24HourPipe,
    DatePipe,
    TimeFormatPipe,
    DefaultValuePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './ipd-discharge.component.html',
  styleUrl: './ipd-discharge.component.scss'
})
export class IpdDischargeComponent implements OnInit, OnDestroy {
  dischargeForm: FormGroup;
  isUpdateMode: boolean = false;
  diagnosis: string = '';
  TemplateType = TemplateType; // Make enum available in template
  staticimagepath: string = environment.imagePath;
  defaultAvatarPath = 'assets/images/profile/user-1.jpg';
  showPatientInfo: boolean = false;
  minDate: Date;
  maxDate: Date = new Date();
  readOnly: boolean = false;
  isLoading: boolean = true;
  @Input() patientDetails: IPatientInfo;
  @Input() opdDetails: Iopd;
  @Input() ipdData: Iipd;

  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    @Optional() private dialogRef: MatDialogRef<IpdDischargeComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      patientDetails?: IPatientInfo,
      opdDetails?: Iopd,
      ipdId?: number,
      ipdData?: Iipd,
      readOnly?: boolean
    },
    private _opdService: OPDService,
    private _ipdService: IpdService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private http: HttpClient,
    private hospitalState: HospitalStateService,
    private userService: UserService,
    private datePipe: DatePipe,
    private timeFormatPipe: TimeFormatPipe,
    private loaderService: LoaderService,
    private defaultValuePipe: DefaultValuePipe,
    private convertTo24HourPipe: ConvertTo24HourPipe,
    private printSettingService: PrintSettingService
  ) {
    this.isUpdateMode = !!this.data?.ipdData;
    this.minDate = new Date(1800, 0, 1);
    this.readOnly = this.data?.readOnly ?? false;
  }

  ngOnInit(): void {
    const now = new Date();
    const currentTime = this.getCurrentTime();

    // Initialize form with current date/time or existing values
    const dischargeDate = this.data?.ipdData?.dischargeDate
      ? new Date(this.data.ipdData.dischargeDate)
      : now;
    const dischargeTime = this.data?.ipdData?.dischargeTime
      ? this.convertTimeSpanToTimeInput(this.data.ipdData.dischargeTime)
      : currentTime;

    this.dischargeForm = this.fb.group({
      dischargeDate: [dischargeDate, Validators.required],
      dischargeTime: [dischargeTime, Validators.required],
      diagnosis: [''],
      finalDiagnosis: [this.data?.ipdData?.finalDiagnosis || null],
      treatmentAdvice: [this.data?.ipdData?.treatmentAdvice || null],
      dischargeCondition: [this.data?.ipdData?.dischargeCondition || null]
    });

    // Load existing IPD data if in update mode
    if (this.isUpdateMode && this.data?.ipdId) {
      this.loadIPDData();
    }

    // Fetch diagnosis from OPD clinical if opdId is available
    if (this.data?.opdDetails?.id) {
      this.loadOPDClinical(this.data.opdDetails.id);
    } else if (this.data?.ipdData?.opdId) {
      this.loadOPDClinical(this.data.ipdData.opdId);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private getCurrentTime(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private convertTimeSpanToTimeInput(timeSpan: string): string {
    if (!timeSpan) return this.getCurrentTime();

    // Handle TimeSpan format from backend (HH:mm:ss or HH:mm:ss.fffffff)
    const timePart = timeSpan.split('.')[0]; // Remove fractional seconds if present
    const parts = timePart.split(':');

    if (parts.length >= 2) {
      const hours = parts[0];
      const minutes = parts[1];
      // Return in HH:mm format for time input
      return `${hours}:${minutes}`;
    }

    return this.getCurrentTime();
  }

  private loadIPDData(): void {
    if (!this.data?.ipdId) return;

    this.isLoading = true;
    this._ipdService.getIPDById(this.data.ipdId)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.result) {
            const ipdData = response.result;
            this.dischargeForm.patchValue({
              dischargeDate: ipdData.dischargeDate ? new Date(ipdData.dischargeDate) : new Date(),
              dischargeTime: ipdData.dischargeTime ? this.convertTimeSpanToTimeInput(ipdData.dischargeTime) : this.getCurrentTime(),
              finalDiagnosis: ipdData.finalDiagnosis || null,
              treatmentAdvice: ipdData.treatmentAdvice || null,
              dischargeCondition: ipdData.dischargeCondition || null
            });
          }
        },
        error: (error) => {
          console.error('Error loading IPD data:', error);
        }
      });
  }

  private loadOPDClinical(opdId: number): void {
    this.isLoading = true;
    this._opdService.getOPDClinical(opdId)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (res: any) => {
          const items = res?.result || [];
          if (items.length === 0) return;

          const diagnosisList = items
            .map((x: any) => x.primaryDiagnosis?.trim() || '')
            .filter((x: string) => x !== '');

          const diagnosisText = diagnosisList.join(', ');
          this.diagnosis = diagnosisText;

          // Update form control
          this.dischargeForm.patchValue({
            diagnosis: diagnosisText
          });
        },
        error: (error) => {
          console.error('Error loading OPD clinical data:', error);
        }
      });
  }

  copyDiagnosisToFinal(): void {
    const diagnosisValue = this.dischargeForm.get('diagnosis')?.value || '';
    this.dischargeForm.patchValue({
      finalDiagnosis: diagnosisValue
    });
  }

  openTemplatePopupforTreatmentAdvice(): void {
    const templateDialog = this.dialog.open(TemplateListComponent, {
      width: '60vw',
      maxWidth: '70vw',
      height: '60vh',
      maxHeight: '75vh',
      disableClose: true,
      data: {
        templateType: TemplateType.TreatmentAdvice
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result && result.selected && result.template) {
        // Get the current value of the treatment advice field
        const currentValue = this.dischargeForm.get('treatmentAdvice')?.value || '';
        const templateText = result.template.templateDescription || '';

        const parts = [currentValue.trim(), templateText.trim()].filter(Boolean);
        const combinedText = parts.join('\n');

        this.dischargeForm.get('treatmentAdvice')?.setValue(combinedText);
      }
    });
  }

  openTemplatePopup(): void {
    const templateDialog = this.dialog.open(TemplateListComponent, {
      width: '60vw',
      maxWidth: '70vw',
      height: '60vh',
      maxHeight: '75vh',
      disableClose: true,
      data: {
        templateType: TemplateType.DischargeCondition
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result && result.selected && result.template) {
        // Get the current value of the discharge condition field
        const currentValue = this.dischargeForm.get('dischargeCondition')?.value || '';
        const templateText = result.template.templateDescription || '';

        const parts = [currentValue.trim(), templateText.trim()].filter(Boolean);
        const combinedText = parts.join('\n');

        this.dischargeForm.get('dischargeCondition')?.setValue(combinedText);
      }
    });
  }

  save(): void {
    if (this.dischargeForm.invalid) {
      this.dischargeForm.markAllAsTouched();
      return;
    }

    if (!this.data?.ipdId) {
      this.toastr.error('IPD ID is missing');
      return;
    }

    this.isLoading = true;
    // Get current IPD data first
    this._ipdService.getIPDById(this.data.ipdId)
      .pipe(
        takeUntil(this._unsubscribeAll),
      )
      .subscribe({
        next: (ipdResponse) => {
          if (!ipdResponse.result) {
            this.isLoading = false;
            this.toastr.error('IPD data not found');
            return;
          }

          const formValue = this.dischargeForm.value;
          const existingIpd = ipdResponse.result;

          // Prepare update payload
          const payload: Iipd = {
            ...existingIpd,
            dischargeDate: new Date(formValue.dischargeDate),
            dischargeTime: this.convertTo24HourPipe.transform(formValue.dischargeTime),
            finalDiagnosis: formValue.finalDiagnosis || null,
            treatmentAdvice: formValue.treatmentAdvice || null,
            dischargeCondition: formValue.dischargeCondition || null
          };

          // Update IPD
          this._ipdService.dischargeIPD(payload)
            .pipe(
              takeUntil(this._unsubscribeAll),
              finalize(() => this.isLoading = false)
            )
            .subscribe({
              next: (response) => {
                if (response.isSuccess) {
                  const message = this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('LABEL.DISCHARGE'));
                  this.toastr.success(message);
                  if (this.dialogRef) {
                    this.dialogRef.close(true);
                  }
                } else {
                  const allErrors = Object.values(response.errors || {}).flat();
                  this.toastr.error(allErrors.join('\n'));
                }
              },
              error: (error) => {
                const errorMessage = error?.error?.message || 'Error updating discharge information';
                this.toastr.error(errorMessage);
              }
            });
        },
        error: (error) => {
          this.isLoading = false;
          const errorMessage = error?.error?.message || 'Error loading IPD data';
          this.toastr.error(errorMessage);
        }
      });
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  async printDischargeDetails(): Promise<void> {
    const ipdId = this.data?.ipdId || this.ipdData?.id;
    const patientInfo = this.data?.patientDetails || this.patientDetails;
    const opdData = this.data?.opdDetails || this.opdDetails;

    if (!ipdId || !patientInfo || !opdData) {
      this.toastr.error('Missing required data for printing');
      return;
    }

    try {
      this.loaderService.show();
      // Load IPD data and operation summary in parallel
      const [ipdResponse, operationSummaryResponse] = await Promise.all([
        this._ipdService.getIPDById(ipdId).pipe(
          catchError(() => of({ result: null, isSuccess: false }))
        ).toPromise(),
        this._ipdService.getOperationSummaryByIpdId(ipdId).pipe(
          catchError(() => of({ result: null, isSuccess: false }))
        ).toPromise()
      ]);

      const loadedIpdData = ipdResponse?.result || this.data?.ipdData || this.ipdData;
      const operationSummary = operationSummaryResponse?.result || null;

      if (!loadedIpdData) {
        this.toastr.error('IPD data not found');
        return;
      }

      const printContent = await this.createDischargePrintContent(loadedIpdData, operationSummary);
      this.openPrintWindow(printContent);
    } catch (error) {
      console.error('Error printing discharge details:', error);
      this.toastr.error('Error loading data for printing');
    } finally {
      this.loaderService.hide();
    }
  }

  private async createDischargePrintContent(ipdData: Iipd, operationSummary: IoperationSummary | null): Promise<string> {
    try {
      let template =
        (await this.http
          .get('assets/print-template.html', { responseType: 'text' })
          .toPromise()) || '';

      const hospital = this.hospitalState.getHospital();
      const opdData = this.data?.opdDetails || this.opdDetails;
      const patientInfo = this.data?.patientDetails || this.patientDetails;
      const doctorId = opdData?.doctorId || ipdData.doctorId;

      const [doctor, settings] = await Promise.all([
        this.userService.getUserByID(doctorId).toPromise(),
        this.printSettingService.getPrintSettingPromise(hospital.id)
      ]);

      const doctorName = `${this.defaultValuePipe.transform(doctor?.result?.firstName, '')} ${this.defaultValuePipe.transform(doctor?.result?.lastName, '')}`.trim();

      // IPD Details Section
      const ipdDetailsHtml = `
        <h2 style="text-align:center; margin:10px 0; text-decoration:underline;">
          IPD DISCHARGE DETAILS
        </h2>

        <div style="display:flex; gap:12px; border:1px solid #000; padding:8px; margin:10px 0;">
          <div style="flex:1; font-size:13px;">
            <strong>Patient Name:</strong>
            ${this.defaultValuePipe.transform(patientInfo?.firstName, '-')} ${this.defaultValuePipe.transform(patientInfo?.lastName, '-')}<br/>
            <strong>Address:</strong>
            ${this.defaultValuePipe.transform(patientInfo?.address?.[0]?.address1, '-')}<br/>
            <strong>Ph:</strong>
            ${this.defaultValuePipe.transform(patientInfo?.contactInfo?.[0]?.contact, '-')}
          </div>
        </div>

        <table style="width:100%; border-collapse:collapse; margin-top:18px; font-size:13px;">
          <tr>
            <td style="border:1px solid #000; padding:6px;">
              <strong>O.P.D No:</strong> ${this.defaultValuePipe.transform(opdData?.opdNumber, '-')}
            </td>
            <td style="border:1px solid #000; padding:6px;">
              <strong>O.P.D Date:</strong>
              ${this.defaultValuePipe.transform(opdData?.opdDate ? this.datePipe.transform(opdData.opdDate, 'dd-MM-yyyy') : null, '-')}
            </td>
            <td style="border:1px solid #000; padding:6px;">
              <strong>Ward:</strong> ${this.defaultValuePipe.transform(ipdData.wardName, '-')}
            </td>
          </tr>
          <tr>
            <td style="border:1px solid #000; padding:6px;">
              <strong>I.P.D No:</strong> ${this.defaultValuePipe.transform(ipdData.ipdNumber, '-')}
            </td>
            <td style="border:1px solid #000; padding:6px;">
              <strong>I.P.D Date:</strong>
              ${this.defaultValuePipe.transform(this.datePipe.transform(ipdData.ipdDate, 'dd-MM-yyyy'), '-')}
            </td>
              <td style="border:1px solid #000; padding:6px;">
              <strong>Room No:</strong> ${this.defaultValuePipe.transform(ipdData.roomNo, '-')}
            </td>
          </tr>
          <tr>
            <td style="border:1px solid #000; padding:6px;">
              <strong>Admitted On:</strong>
              ${this.defaultValuePipe.transform(this.datePipe.transform(ipdData.ipdDate, 'dd-MM-yyyy'), '-')}
            </td>
            <td style="border:1px solid #000; padding:6px;">
              <strong>Admitted At:</strong> ${this.defaultValuePipe.transform(this.timeFormatPipe.transform(ipdData.ipdTime), '-')}
            </td>
             <td style="border:1px solid #000; padding:6px;">
              <strong>Bed No:</strong> ${this.defaultValuePipe.transform(ipdData.bedNo, '-')}
            </td>
          </tr>
          <tr>
            <td style="border:1px solid #000; padding:6px;">
              <strong>Discharge Date:</strong>
              ${this.defaultValuePipe.transform(ipdData.dischargeDate ? this.datePipe.transform(ipdData.dischargeDate, 'dd-MM-yyyy') : null, '-')}
            </td>
            <td style="border:1px solid #000; padding:6px;">
              <strong>Discharge Time:</strong>
              ${this.defaultValuePipe.transform(ipdData.dischargeTime ? this.timeFormatPipe.transform(ipdData.dischargeTime) : null, '-')}
            </td>
            <td style="border:1px solid #000; padding:6px;"></td>
          </tr>
        </table>

        <div style="margin-top:15px; font-size:13px;">
          <strong>Assistant Doctor:</strong>
          ${this.defaultValuePipe.transform(ipdData.assitantDoctorName, '-')}
        </div>

        <div style="margin-top:15px; font-size:13px;">
          <strong>Anesthetist:</strong>
          ${this.defaultValuePipe.transform(ipdData.anesthetisName, '-')}
        </div>

        <div style="margin-top:15px; font-size:13px;">
          <strong>Provisional Diagnosis:</strong><br/>
          <span style="white-space:pre-wrap;">${this.defaultValuePipe.transform(ipdData.provisionalDiagnosis, '-')}</span>
        </div>

        ${ipdData.finalDiagnosis ? `
        <div style="margin-top:15px; font-size:13px;">
          <strong>Diagnosis at Discharge:</strong><br/>
          <span style="white-space:pre-wrap;">${ipdData.finalDiagnosis}</span>
        </div>
        ` : ''}

        ${operationSummary?.investigation ? `
            <div style="margin-top:15px; font-size:13px;">
              <strong>Investigation:</strong><br/>
              <span style="white-space:pre-wrap;">${operationSummary.investigation}</span>
            </div>
            ` : ''}

        ${operationSummary?.treatmentGiven ? `
            <div style="margin-top:15px; font-size:13px;">
              <strong>Treatment Given:</strong><br/>
              <span style="white-space:pre-wrap;">${operationSummary.treatmentGiven}</span>
            </div>
        ` : ''}

        ${operationSummary?.natureOfTreatmentName ? `
            <div style="margin-top:15px; font-size:13px;">
              <strong>Nature of Treatment:</strong><br/>
              <span style="white-space:pre-wrap;">${operationSummary.natureOfTreatmentName}</span>
            </div>
        ` : ''}

        ${ipdData.treatmentAdvice ? `
        <div style="margin-top:15px; font-size:13px;">
          <strong>Treatment Advice:</strong><br/>
          <span style="white-space:pre-wrap;">${ipdData.treatmentAdvice}</span>
        </div>
        ` : ''}

        ${ipdData.dischargeCondition ? `
        <div style="margin-top:15px; font-size:13px;">
          <strong>Discharge Condition:</strong><br/>
          <span style="white-space:pre-wrap;">${ipdData.dischargeCondition}</span>
        </div>
        ` : ''}

        ${ipdData.nameAndAddress ? `
        <div style="margin-top:15px; font-size:13px;">
          <strong>Name & Address of relative / friend attending patient:</strong><br/>
          <span style="white-space:pre-wrap;">${ipdData.nameAndAddress}</span><br/>
          <strong>Tel No:</strong> ${this.defaultValuePipe.transform(ipdData.contact, '-')}
        </div>
        ` : ''}
      `;

      const reportSections = ipdDetailsHtml;

      const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;
      const fullImagePath = hospital?.logo ? `${this.staticimagepath}${hospital.logo}` : null;

      const replacements: Record<string, string> = {
        reportTitle: `Discharge Details - ${this.defaultValuePipe.transform(patientInfo?.firstName, '-')} ${this.defaultValuePipe.transform(patientInfo?.lastName, '-')}`,
        hospitalName: this.defaultValuePipe.transform(hospital?.name, 'Hospital Name'),
        hospitalAddress: this.defaultValuePipe.transform(hospital?.address, ''),
        phone: this.defaultValuePipe.transform(hospital?.phone, ''),
        reportSections: reportSections,
        doctorName: doctorName,
        qualification: this.defaultValuePipe.transform(doctor?.result?.degree, ''),
        doctorRegistrationNo: doctor?.result?.registrationNumber 
          ? `Reg No. ${this.defaultValuePipe.transform(doctor?.result?.registrationNumber, '')}` 
          : '',
        doctorPhone: doctor?.result?.phoneNumber 
          ? `M. ${this.defaultValuePipe.transform(doctor?.result?.phoneNumber, '')}` 
          : '',
        logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
        patientName: `${this.defaultValuePipe.transform(patientInfo?.firstName, '-')} ${this.defaultValuePipe.transform(patientInfo?.lastName, '-')}`,
        patientAge: `${this.defaultValuePipe.transform(patientInfo?.age, '-')} / ${this.defaultValuePipe.transform(patientInfo?.gender, '-')}`,
        opdNumber: this.defaultValuePipe.transform(opdData?.opdNumber, '-'),
        opdDate: this.defaultValuePipe.transform(opdData?.opdDate ? this.datePipe.transform(opdData.opdDate, 'dd-MM-yyyy') : null, '-')
      };

      let filledTemplate = template;
      for (const key in replacements) {
        const value = replacements[key] ?? '';
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        filledTemplate = filledTemplate.replace(regex, value);
      }

      // Apply settings
      let styles = '';
      if (settings) {
        const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
        const headerClass = settings.logoPosition === 'center' ? 'center' : '';
        const headerAlignment = settings.addressAlignment || 'left';

        styles = `
            :root {
                --logo-size: ${settings.logoSize || '60px'};
                --logo-position: ${logoPos};
                --header-alignment: ${headerAlignment};
                --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                --address-font-size: ${settings.addressFontSize || 13}px;
                --footer-display: ${settings.showFooter ? 'block' : 'none'};
                --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
            }
        `;
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
      } else {
        filledTemplate = filledTemplate.replace('{{headerClass}}', '');
      }

      return filledTemplate;
    } catch (error) {
      console.error('Error creating discharge print content:', error);
      throw error;
    }
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }
}
